import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import QueryString from 'qs';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import useAxios from '../../../hooks/useAxios';
import {
  IGeneralMessageParams,
  IGetGeneralMessage,
} from '../../../models/pushMessage.model';
import { getGeneralMessages } from '../../../api/pushMessage.api';
import { DatePicker, Select, Table, TableColumnsType } from 'antd';
import moment from 'moment/moment';
import Loader from '../../../components/Loader';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import SelectCampus from '../../../components/SelectCampus';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import Pagination from '../../../components/Pagination';
import useUtil from '../../../hooks/useUtil';

const SmsNoMember: FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetGeneralMessage>();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);

  const [page, setPage] = useState(1);
  const [param, setParam] = useState<IGeneralMessageParams>({});
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sendType, setSendType] = useState('ALL');
  const [sendTimeDiv, setSendTimeDiv] = useState('ALL');
  const [senderName, setSenderName] = useState('');
  const [campusDeactived, setCampusDeactived] = useState(false);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (serverDate && !Object.keys(query).length) {
      const _srtDate = moment(serverDate)
        .subtract(30, 'days')
        .format('YYYYMMDD');
      const _endDate = moment(serverDate).format('YYYYMMDD');

      const param: any = { page: 1 };
      param.srtDate = _srtDate;
      param.endDate = _endDate;
      setParam(param);
      history.push(`/sms/nonmember?${QueryString.stringify(param)}`);
    }
  }, [serverDate]);

  useEffect(() => {
    if (!Object.keys(query).length) return;

    const _param: IGeneralMessageParams = { campusCode: currentCampus };

    if (query.page) {
      _param.pageNo = Number(query.page);
      setPage(_param.pageNo);
    }
    if (query.srtDate) {
      _param.srtDate = String(query.srtDate);
      setSrtDate(_param.srtDate);
    }
    if (query.endDate) {
      _param.endDate = String(query.endDate);
      setEndDate(_param.endDate);
    }
    if (query.sendType) {
      _param.sendType = String(query.sendType);
      setSendType(_param.sendType);
    }
    if (query.sendTimeDiv) {
      _param.sendTimeDiv = String(query.sendTimeDiv);
      setSendTimeDiv(_param.sendTimeDiv);
    }
    if (query.senderName) {
      _param.senderName = String(query.senderName);
      setSenderName(_param.senderName);
    }

    execute(getGeneralMessages(_param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    setParam(_param);
  }, [location]);

  const onSearch = () => {
    const param: any = { page: 1 };

    if (srtDate && endDate) {
      param.srtDate = srtDate;
      param.endDate = endDate;
    }
    if (sendType !== 'ALL') param.sendType = sendType;
    if (sendTimeDiv !== 'ALL') param.sendTimeDiv = sendTimeDiv;
    if (senderName) param.senderName = senderName;

    setParam(param);
    history.push(`/sms/nonmember?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusName',
      key: 'campusName',
      align: 'center',
      width: '10%',
    },
    {
      title: t('sms.list.column15'),
      dataIndex: 'sendDatetime',
      key: 'sendDatetime',
      align: 'center',
      width: '10%',
      render: (value) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('sms.list.column2'),
      key: 'sendType',
      dataIndex: 'sendType',
      align: 'center',
      width: '6%',
      render: (value) => {
        switch (value) {
          case 'GE':
            return t('student.student.option20');
          case 'AD':
            return t('student.student.option21');
          default:
            return '';
        }
      },
    },
    {
      title: t('sms.list.column5'),
      key: 'senderName',
      dataIndex: 'senderName',
      align: 'center',
      width: '10%',
      render: (value, record) =>
        i18n.language === 'ko' ? value : record.memberEngName,
    },
    {
      title: t('common.column.title'),
      key: 'title',
      dataIndex: 'title',
      align: 'center',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('common.column.contents'),
      key: 'content',
      dataIndex: 'content',
      align: 'center',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('sms.list.column16'),
      key: 'sendTimeDiv',
      dataIndex: 'sendTimeDiv',
      align: 'center',
      width: '10%',
      render: (value) => {
        switch (value) {
          case 'IM':
            return t('student.student.option22');
          case 'RE':
            return t('student.student.option23');
          case 'CN':
            return t('sms.list.option1');
          default:
            return '';
        }
      },
    },
    {
      title: t('sms.list.column17'),
      dataIndex: 'sendDateTime',
      align: 'center',
      width: '10%',
      render: (value, record) => {
        if (record.sendTimeDiv === 'RE') {
          return moment(value).format('YYYY.MM.DD HH:mm:ss');
        }
      },
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-sms-big.svg"
      parentMenu={t('common.menu.text')}
      currentMenu={t('sms.list.title4')}
      onlyTitle={true}
    />	
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('sms.list.column1')}</label>
            <div className="date-picker__area">
              <div>
                <DatePicker
                  value={srtDate ? moment(srtDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setSrtDate('');
                    else setSrtDate(moment(e).format('YYYYMMDD'));
                  }}
                />
              </div>
              ~
              <div>
                <DatePicker
                  value={endDate ? moment(endDate) : undefined}
                  onChange={(e) => {
                    if (e === null) setEndDate('');
                    else setEndDate(moment(e).format('YYYYMMDD'));
                  }}
                />
              </div>
            </div>
          </li>
          <li>
            <label>{t('sms.list.column2')}</label>
            <div>
              <Select
                value={sendType}
                onChange={(value) => setSendType(value)}
              >
                <Select.Option value="ALL">
                  {t('common.text.all')}
                </Select.Option>
                <Select.Option value="GE">
                  {t('student.student.option20')}
                </Select.Option>
                <Select.Option value="AD">
                  {t('student.student.option21')}
                </Select.Option>
              </Select>
            </div>
          </li>
          <li>
            <label>{t('sms.list.column3')}</label>
            <div>
              <Select
                value={sendTimeDiv}
                onChange={(value) => setSendTimeDiv(value)}
              >
                <Select.Option value="ALL">
                  {t('common.text.all')}
                </Select.Option>
                <Select.Option value="IM">
                  {t('student.student.option22')}
                </Select.Option>
                <Select.Option value="RE">
                  {t('student.student.option23')}
                </Select.Option>
                <Select.Option value="CN">
                  {t('sms.list.option1')}
                </Select.Option>
              </Select>
            </div>
          </li>
          <li>
            <label>{t('sms.list.column5')}</label>
            <input
              type="text"
              className="form-control w-150p"
              placeholder={t('sms.list.column5')}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
            />
            <div className="comm-btn__area">
            <Button
              className="btn-basic small black"
              onClick={onSearch}
              text={t('common.button.search')}
            />
            <Button
              className="btn-basic pink-line"
              onClick={() => history.push('/sms/nonmember/send')}
              disabled={campusDeactived}
              text={t('student.student.button10')}
            />
            </div>
          </li>
          <li></li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      
      <div className="col-frame">
        <div className='comm-bx__between'>
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{res?.total ?? 0}</b>
            {t('common.text.unit')}
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data ?? []}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/sms/nonmember/${record.smsSendCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName="pointer"
            rowKey="smsSendCode"
          />
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param}
              path="/sms/nonmember"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SmsNoMember;
